import { useEffect, useRef, useState } from 'react';
import { CopyTooltip } from '@overlayqa/shared/ui';
import { HugeiconsIcon } from '@hugeicons/react';
import { Tick02Icon, Copy01Icon } from '@hugeicons/core-free-icons';
import { copyText } from '@/lib/copyText';

interface IssueIdWithCopyProps {
  /** Human-readable ID like "OQ-127". When absent, the fallback shows. */
  displayId?: string;
  /** Fallback text rendered when displayId is unavailable, e.g. "#af9faeb4". */
  fallback: string;
  /** Optional class applied to the outer button. */
  className?: string;
}

/**
 * Renders the issue identifier with a single-click copy affordance.
 * Mirrors Linear's pattern: tap the chip to copy the canonical ID to
 * clipboard, get a brief check-mark confirmation.
 *
 * Only copies displayId when present. Falls back to the truncated UUID
 * label for any ticket that somehow has no displayId.
 */
export function IssueIdWithCopy({ displayId, fallback, className = '' }: IssueIdWithCopyProps) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  const label = displayId ?? fallback;
  const copied = copyState === 'copied';
  const failed = copyState === 'failed';

  const handleCopy = async () => {
    clearTimeout(timer.current);
    setCopyState('idle');
    // copyText (OQA-11) resolves a boolean so the caller always shows
    // success OR failure feedback - the old inline handler failed silently.
    const ok = await copyText(label);
    setCopyState(ok ? 'copied' : 'failed');
    timer.current = setTimeout(() => setCopyState('idle'), 1500);
  };

  return (
    <CopyTooltip label={`Copy ${label}`} copied={copied} failed={failed}>
    <button
      type="button"
      onClick={handleCopy}
      className={`inline-flex items-center gap-2 -mx-1 px-1 py-1 rounded hover:bg-gray-100 transition-colors group ${className}`}
      aria-label={copied ? 'Copied issue ID' : failed ? 'Copy failed' : `Copy issue ID ${label}`}
    >
      <span>{label}</span>
      {copied ? (
        <HugeiconsIcon icon={Tick02Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5 text-emerald-600" aria-hidden="true" />
      ) : failed ? (
        <HugeiconsIcon icon={Copy01Icon} size={14} strokeWidth={2} color="currentColor" className="w-3.5 h-3.5 text-red-600" aria-hidden="true" />
      ) : (
        <HugeiconsIcon icon={Copy01Icon} size={14} strokeWidth={1.5} color="currentColor" className="w-3.5 h-3.5 text-gray-400 opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity" aria-hidden="true" />
      )}
    </button>
    </CopyTooltip>
  );
}
